/**
 * Document Download Service
 * Handles decryption and download of encrypted documents
 * Uses symmetric AES-256-GCM decryption with master key from session
 */

import { supabase } from '@xanectech/supabase-client';
import {
  getCachedMasterKey,
  decryptFileBinary,
  unwrapDocumentKeyWithMaster,
} from '@xanectech/encryption';
import { createLogger } from '@xanectech/lib';
import type { UserDocument } from '@xanectech/types';

const logger = createLogger('DocumentDownloadService');

interface EncryptedKeyBundle {
  encryptedKey: string;
  keyIv: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseEncryptedKeyBundle(payload: string): EncryptedKeyBundle {
  const parsed: unknown = JSON.parse(payload);

  if (!isObject(parsed)) {
    throw new Error('Invalid encrypted key format');
  }

  const encryptedKey = parsed.encryptedKey;
  const keyIv = parsed.keyIv;

  if (typeof encryptedKey !== 'string' || typeof keyIv !== 'string') {
    throw new Error('Invalid encrypted key format');
  }

  return {
    encryptedKey,
    keyIv,
  };
}

/**
 * Download and decrypt a document
 */
export async function downloadDocument(document: UserDocument): Promise<Blob> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) {
    throw new Error('Not authenticated');
  }

  // Verify document is encrypted
  if (!document.is_encrypted || !document.encrypted_key || !document.iv) {
    throw new Error('Document is not properly encrypted');
  }

  // Get the master key from the encryption session
  const masterKey = getCachedMasterKey(userData.user.id);
  if (!masterKey) {
    throw new Error(
      'Encryption session not active. Please unlock your vault first.',
    );
  }

  // Download encrypted file
  const { data: encryptedData, error: downloadError } = await supabase.storage
    .from(document.storage_bucket)
    .download(document.storage_path);

  if (downloadError) {
    logger.error('Failed to download encrypted file', downloadError);
    throw new Error('Failed to download file');
  }

  // Parse the encrypted key bundle
  const { encryptedKey, keyIv } = parseEncryptedKeyBundle(
    document.encrypted_key,
  );

  // Unwrap the document key using the master key
  const documentKey = await unwrapDocumentKeyWithMaster(
    String(encryptedKey),
    String(keyIv),
    masterKey,
  );

  // Decrypt the file
  const encryptedBuffer = await encryptedData.arrayBuffer();
  const decryptedData = await decryptFileBinary(
    encryptedBuffer,
    document.iv,
    documentKey,
  );

  // Return as blob with original mime type
  return new Blob([decryptedData], {
    type: document.mime_type || 'application/octet-stream',
  });
}

/**
 * Download and save a document to user's device
 */
export async function downloadAndSaveDocument(
  document: UserDocument,
): Promise<void> {
  try {
    const blob = await downloadDocument(document);

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = url;
    link.download = document.file_name;

    // Trigger download
    window.document.body.appendChild(link);
    link.click();

    // Cleanup
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Log access
    await logDocumentAccess(document.id, document.user_id, 'download');

  } catch (error) {
    const errorMessage =
      error instanceof Error ? error : new Error(String(error));
    logger.error('Failed to download document', errorMessage);
    throw error;
  }
}

/**
 * Get a preview URL for a document (for images/PDFs)
 */
export async function getDocumentPreviewUrl(
  document: UserDocument,
): Promise<string | null> {
  try {
    const blob = await downloadDocument(document);

    // Log access
    await logDocumentAccess(document.id, document.user_id, 'view');

    return URL.createObjectURL(blob);
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error : new Error(String(error));
    logger.error('Failed to get document preview', errorMessage);
    return null;
  }
}

/**
 * Log document access for audit trail
 */
async function logDocumentAccess(
  documentId: string,
  documentOwnerId: string,
  accessType: 'view' | 'download' | 'share' | 'edit' | 'delete',
): Promise<void> {
  try {
    let ownerId = documentOwnerId;
    if (!ownerId) {
      const { data: ownerRow, error: ownerError } = await supabase
        .from('user_documents')
        .select('user_id')
        .eq('id', documentId)
        .single();

      if (ownerError) {
        logger.error('Failed to resolve document owner for access log', {
          documentId,
          ownerError,
        });
      }

      ownerId = ownerRow?.user_id || '';
    }

    if (!ownerId) {
      logger.error('Skipping document access log: missing owner user_id', {
        documentId,
        accessType,
      });
      return;
    }

    const { error } = await supabase.from('document_access_log').insert({
      document_id: documentId,
      user_id: ownerId,
      accessed_by: (await supabase.auth.getUser()).data.user?.id,
      access_type: accessType,
      access_via: 'direct',
      access_granted: true,
    });

    if (error) {
      logger.error('Failed to log document access', error);
    }
  } catch (error) {
    // Don't throw - logging failures shouldn't break the flow
    const errorMessage =
      error instanceof Error ? error : new Error(String(error));
    logger.error('Failed to log document access', errorMessage);
  }
}
